import { useState, useEffect, useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { usePartnerAuth } from '../../context/PartnerAuthContext';
import { supabasePartner } from '../../lib/supabase';
import { downloadTicket } from '../../utils/ticketPDF';
import { displayDeck } from '../../components/shared/helpers';

const PAGE_SIZE = 20;
const CRUISE_TYPES = ['Sunset Cruise', 'Sundown Cruise', 'Dinner Cruise'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Booking dates are stored as 'DD-MM-YYYY' text. Parse to a LOCAL midnight Date
// so date-only comparisons (today, range bounds) are consistent and TZ-safe.
function parseDdMmYyyy(s) {
  if (!s) return null;
  const [dd, mm, yyyy] = String(s).split('-');
  const d = Number(dd), m = Number(mm), y = Number(yyyy);
  if (!d || !m || !y) return null;
  return new Date(y, m - 1, d);
}

// 'DD-MM-YYYY' → '05 Jul 2026' for display.
function fmtDisplayDate(s) {
  const d = parseDdMmYyyy(s);
  if (!d) return s || '—';
  return `${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

// ISO 'YYYY-MM-DD' (from a date input) → LOCAL midnight Date, or null.
function parseIso(s) {
  if (!s) return null;
  const [y, m, d] = String(s).split('-');
  const yy = Number(y), mm = Number(m), dd = Number(d);
  if (!yy || !mm || !dd) return null;
  return new Date(yy, mm - 1, dd);
}

// Today at local midnight — the pivot for the Past / Upcoming toggle.
function todayMidnight() {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), n.getDate());
}

// Compact passenger summary, zero types omitted: "2A · 1C · 1I".
function paxLabel(b) {
  const parts = [];
  const a = Number(b.adults) || 0;
  const c = Number(b.children) || 0;
  const i = Number(b.infants) || 0;
  if (a > 0) parts.push(`${a}A`);
  if (c > 0) parts.push(`${c}C`);
  if (i > 0) parts.push(`${i}I`);
  return parts.length ? parts.join(' · ') : '—';
}

export default function PartnerBookings() {
  const { partnerSession, partnerProfile, loadingAuth } = usePartnerAuth();
  const navigate = useNavigate();

  const partnerId = partnerProfile?.id ?? null;

  // Combined booking rows (booking + resolved agent name + reference), or null
  // while loading.
  const [rows, setRows] = useState(null);
  const [loadError, setLoadError] = useState('');

  // Filters
  const [timeFilter, setTimeFilter] = useState('all'); // 'all' | 'upcoming' | 'past'
  const [fromDate, setFromDate] = useState('');         // ISO YYYY-MM-DD
  const [toDate, setToDate] = useState('');             // ISO YYYY-MM-DD
  const [cruiseFilter, setCruiseFilter] = useState('all');

  const [page, setPage] = useState(1);

  // Per-row ticket download state (id being prepared) + inline error.
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadErrId, setDownloadErrId] = useState(null);

  // Load once: this partner's bookings + their agents + the references for these
  // bookings, resolved into a single list in memory. partner_id-scoped.
  useEffect(() => {
    if (loadingAuth) return;
    if (!partnerSession || partnerProfile?.approval_status !== 'approved') return;
    if (!partnerId) return;

    let cancelled = false;
    (async () => {
      setLoadError('');
      const { data: bookings, error: bErr } = await supabasePartner
        .from('bookings')
        .select('*')
        .eq('partner_id', partnerId)
        .order('created_at', { ascending: false });

      if (cancelled) return;
      if (bErr) {
        setLoadError('We could not load your bookings. Please try again.');
        setRows([]);
        return;
      }

      const list = bookings || [];
      const bookingIds = list.map((b) => b.id);

      // Agents (id → name) and references (booking_id → reference_number) for
      // these bookings.
      const [agentsRes, refsRes] = await Promise.all([
        supabasePartner
          .from('partner_agents')
          .select('id, agent_name')
          .eq('partner_id', partnerId),
        bookingIds.length
          ? supabasePartner
              .from('booking_references')
              .select('booking_id, reference_number')
              .in('booking_id', bookingIds)
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (cancelled) return;

      const agentName = new Map((agentsRes.data || []).map((a) => [a.id, a.agent_name]));
      const refFor = new Map((refsRes.data || []).map((r) => [r.booking_id, r.reference_number]));

      setRows(
        list.map((b) => ({
          ...b,
          _agentName: b.agent_id ? (agentName.get(b.agent_id) || null) : null,
          _reference: refFor.get(b.id) || null,
        })),
      );
    })();

    return () => { cancelled = true; };
  }, [loadingAuth, partnerSession, partnerProfile, partnerId]);

  // Apply all filters (AND) in memory over the loaded list.
  const filtered = useMemo(() => {
    if (!rows) return [];
    const today = todayMidnight();
    const from = parseIso(fromDate);
    const to = parseIso(toDate);
    return rows.filter((b) => {
      const d = parseDdMmYyyy(b.date);
      // Time toggle
      if (timeFilter !== 'all') {
        if (!d) return false;
        if (timeFilter === 'upcoming' && d < today) return false;
        if (timeFilter === 'past' && d >= today) return false;
      }
      // Date range (inclusive)
      if (from) { if (!d || d < from) return false; }
      if (to) { if (!d || d > to) return false; }
      // Cruise type
      if (cruiseFilter !== 'all' && b.cruise_type !== cruiseFilter) return false;
      return true;
    });
  }, [rows, timeFilter, fromDate, toDate, cruiseFilter]);

  // Reset to page 1 whenever the filtered set changes shape.
  useEffect(() => { setPage(1); }, [timeFilter, fromDate, toDate, cruiseFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const hasActiveFilters = timeFilter !== 'all' || fromDate !== '' || toDate !== '' || cruiseFilter !== 'all';
  const clearFilters = () => {
    setTimeFilter('all');
    setFromDate('');
    setToDate('');
    setCruiseFilter('all');
  };

  const handleDownload = async (b) => {
    if (downloadingId) return;
    setDownloadErrId(null);
    setDownloadingId(b.id);
    try {
      await downloadTicket(
        {
          id: b.id,
          booking_name: b.booking_name || 'Guest',
          cruise_type: b.cruise_type || '',
          deck: b.deck || '',
          date: b.date || '',
          adults: b.adults,
          children: b.children,
          infants: b.infants,
          tour_leaders: 0,
        },
        b._reference || null,
      );
    } catch (err) {
      console.error('Partner ticket download failed:', err);
      setDownloadErrId(b.id);
    } finally {
      setDownloadingId(null);
    }
  };

  // Guards — wait for the auth check, then require an approved partner session.
  if (loadingAuth) {
    return (
      <div className="w-full max-w-[480px] p-px rounded-2xl bg-gradient-to-b from-[rgba(212,168,67,0.32)] via-[rgba(255,255,255,0.06)] to-[rgba(255,255,255,0.02)] card-enter">
        <div className="bg-[#0D1E35] rounded-[15px] shadow-[0_40px_80px_rgba(0,0,0,0.55)] px-7 py-8">
          <p className="font-dm-sans text-sm text-white/55 font-light leading-relaxed text-center">
            Loading...
          </p>
        </div>
      </div>
    );
  }
  if (!partnerSession) return <Navigate to="/partner/login" replace />;
  if (partnerProfile?.approval_status !== 'approved') return <Navigate to="/partner" replace />;

  const loading = rows === null;
  const noBookingsAtAll = !loading && rows.length === 0;

  return (
    <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6 card-enter">

      {/* Header row: back to dashboard · heading */}
      <div className="flex items-center gap-3 mb-5">
        <button
          type="button"
          onClick={() => navigate('/partner')}
          className="flex items-center justify-center w-8 h-8 rounded-full border border-[rgba(212,168,67,0.3)] hover:border-[rgba(212,168,67,0.65)] hover:bg-[rgba(212,168,67,0.06)] transition-[border-color,background-color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] shrink-0"
          aria-label="Back to dashboard"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M9 2L4 7L9 12" stroke="#D4A843" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div>
          <p className="font-dm-sans text-[12px] uppercase tracking-[0.18em] text-[rgba(201,168,76,0.85)]">
            Partner Portal
          </p>
          <h1 className="font-cormorant font-normal text-[2rem] md:text-[2.4rem] leading-[1.05] tracking-[-0.01em] text-white">
            My Bookings
          </h1>
        </div>
      </div>

      {loading ? (
        <p className="font-dm-sans text-sm text-white/55 font-light text-center py-16 leading-relaxed">
          Loading your bookings...
        </p>
      ) : loadError ? (
        <div className="rounded-xl border border-[rgba(212,168,67,0.4)] bg-[rgba(201,168,76,0.08)] px-5 py-6 text-center">
          <p className="font-dm-sans text-sm font-light text-white/75 leading-relaxed">{loadError}</p>
        </div>
      ) : noBookingsAtAll ? (
        <div className="rounded-xl border border-white/[0.09] bg-[#162840] px-6 py-12 text-center">
          <p className="font-dm-sans text-base font-light text-white/70 leading-relaxed mb-5">
            You haven&rsquo;t made any bookings yet.
          </p>
          <button
            type="button"
            onClick={() => navigate('/partner/book/agent')}
            className="inline-flex items-center justify-center px-7 py-[13px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full active:scale-[0.97] hover:bg-[#B8973E] transition-[transform,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A1628]"
          >
            Book a Cruise
          </button>
        </div>
      ) : (
        <>
          {/* Filter bar */}
          <div className="rounded-xl border border-white/[0.09] bg-[#162840] p-4 mb-5 flex flex-col gap-4">
            <div className="flex flex-wrap items-end gap-x-6 gap-y-4">

              {/* Time toggle */}
              <div className="flex flex-col gap-1.5">
                <span className="font-dm-sans text-[12px] uppercase tracking-[0.1em] font-medium text-[rgba(212,168,67,0.85)]">When</span>
                <div className="inline-flex rounded-full border border-white/15 overflow-hidden">
                  {[['all', 'All'], ['upcoming', 'Upcoming'], ['past', 'Past']].map(([val, label], i) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setTimeFilter(val)}
                      className={`font-dm-sans text-[13px] font-medium px-3.5 py-1.5 transition-[background-color,color] duration-150 ${i > 0 ? 'border-l border-white/15' : ''} ${
                        timeFilter === val ? 'bg-[#C9A84C] text-[#0A1628]' : 'bg-transparent text-white/60 hover:text-white/90'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Date range */}
              <div className="flex flex-col gap-1.5">
                <span className="font-dm-sans text-[12px] uppercase tracking-[0.1em] font-medium text-[rgba(212,168,67,0.85)]">From</span>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  onClick={(e) => e.target.showPicker && e.target.showPicker()}
                  className="font-dm-sans text-[13px] text-white bg-[#0D1E35] border border-white/15 rounded-lg px-3 py-1.5 outline-none focus:border-[#C9A84C] transition-colors duration-200 [&::-webkit-calendar-picker-indicator]:opacity-80 [&::-webkit-calendar-picker-indicator]:hover:opacity-100 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="font-dm-sans text-[12px] uppercase tracking-[0.1em] font-medium text-[rgba(212,168,67,0.85)]">To</span>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  onClick={(e) => e.target.showPicker && e.target.showPicker()}
                  className="font-dm-sans text-[13px] text-white bg-[#0D1E35] border border-white/15 rounded-lg px-3 py-1.5 outline-none focus:border-[#C9A84C] transition-colors duration-200 [&::-webkit-calendar-picker-indicator]:opacity-80 [&::-webkit-calendar-picker-indicator]:hover:opacity-100 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                />
              </div>

              {/* Cruise type */}
              <div className="flex flex-col gap-1.5">
                <span className="font-dm-sans text-[12px] uppercase tracking-[0.1em] font-medium text-[rgba(212,168,67,0.85)]">Cruise</span>
                <select
                  value={cruiseFilter}
                  onChange={(e) => setCruiseFilter(e.target.value)}
                  className="font-dm-sans text-[13px] text-white bg-[#0D1E35] border border-white/15 rounded-lg px-3 py-1.5 outline-none focus:border-[#C9A84C] transition-colors duration-200 [color-scheme:dark]"
                >
                  <option value="all">All cruises</option>
                  {CRUISE_TYPES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center justify-between gap-4 border-t border-white/[0.06] pt-3">
              <span className="font-dm-sans text-[13px] font-light text-white/55">
                {filtered.length} {filtered.length === 1 ? 'booking' : 'bookings'}
                {hasActiveFilters ? ' match your filters' : ''}
              </span>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="font-dm-sans text-[13px] font-medium text-[#C9A84C] hover:text-[#B8973E] hover:underline underline-offset-2 transition-colors duration-200"
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="rounded-xl border border-white/[0.09] bg-[#162840] px-6 py-12 text-center">
              <p className="font-dm-sans text-base font-light text-white/70 leading-relaxed mb-4">
                No bookings match your filters.
              </p>
              <button
                type="button"
                onClick={clearFilters}
                className="font-dm-sans text-[13px] font-medium text-[#C9A84C] hover:text-[#B8973E] hover:underline underline-offset-2 transition-colors duration-200"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <>
              {/* Table — horizontally scrollable on mobile, desktop primary */}
              <div className="overflow-x-auto rounded-xl border border-white/[0.09]">
                <table className="w-full min-w-[860px] border-collapse">
                  <thead>
                    <tr className="bg-[#162840]">
                      {['Date', 'Cruise', 'Deck', 'Pax', 'Amount', 'Agent', 'Status', 'Reference', 'Ticket'].map((h) => (
                        <th
                          key={h}
                          className={`font-dm-sans text-[11px] uppercase tracking-[0.1em] font-semibold text-[rgba(212,168,67,0.85)] px-4 py-3 whitespace-nowrap ${h === 'Amount' ? 'text-right' : 'text-left'}`}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((b) => (
                      <tr key={b.id} className="border-t border-white/[0.06] hover:bg-white/[0.02] transition-colors duration-150">
                        <td className="font-dm-sans text-[13px] font-light text-white px-4 py-3 whitespace-nowrap">
                          {fmtDisplayDate(b.date)}
                        </td>
                        <td className="font-dm-sans text-[13px] font-light text-white px-4 py-3 whitespace-nowrap">
                          {b.cruise_type || '—'}
                        </td>
                        <td className="font-dm-sans text-[13px] font-light text-white/80 px-4 py-3 whitespace-nowrap">
                          {displayDeck(b.cruise_type, b.deck) || '—'}
                        </td>
                        <td className="font-dm-sans text-[13px] font-light text-white/80 px-4 py-3 whitespace-nowrap tabular-nums">
                          {paxLabel(b)}
                        </td>
                        <td className="font-dm-sans text-[13px] font-medium text-[#C9A84C] px-4 py-3 whitespace-nowrap text-right tabular-nums">
                          &#8377;{(Number(b.total_amount) || 0).toLocaleString('en-IN')}
                        </td>
                        <td className="font-dm-sans text-[13px] font-light text-white/80 px-4 py-3 whitespace-nowrap">
                          {b._agentName || '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="inline-block font-dm-sans text-[11px] font-medium tracking-[0.04em] rounded-full px-2.5 py-1 border border-[rgba(201,168,76,0.4)] bg-[rgba(201,168,76,0.1)] text-[#C9A84C]">
                            {b.payment_status || '—'}
                          </span>
                        </td>
                        <td className="font-dm-sans text-[13px] font-light text-white/80 px-4 py-3 whitespace-nowrap tabular-nums">
                          {b._reference || '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => handleDownload(b)}
                            disabled={downloadingId === b.id}
                            className="font-dm-sans text-[12px] font-semibold tracking-[0.04em] text-[#0A1628] bg-[#C9A84C] rounded-full px-3.5 py-1.5 active:scale-[0.97] hover:bg-[#B8973E] transition-[transform,background-color,opacity] duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {downloadingId === b.id ? 'Preparing…' : 'Download'}
                          </button>
                          {downloadErrId === b.id && (
                            <span className="block font-dm-sans text-[11px] font-light text-[rgba(212,168,67,0.75)] mt-1">
                              Couldn&rsquo;t prepare. Try again.
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between gap-4 mt-4 mb-2">
                <span className="font-dm-sans text-[13px] font-light text-white/55">
                  Page {safePage} of {totalPages}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={safePage <= 1}
                    className="font-dm-sans text-[13px] font-medium text-white/70 border border-white/15 rounded-full px-4 py-1.5 transition-[border-color,color,opacity] duration-150 hover:border-white/30 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safePage >= totalPages}
                    className="font-dm-sans text-[13px] font-medium text-white/70 border border-white/15 rounded-full px-4 py-1.5 transition-[border-color,color,opacity] duration-150 hover:border-white/30 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      )}

    </div>
  );
}
